import { CalendarDays, Users, Flame } from "lucide-react";
import { motion } from "framer-motion";
import PageHeader from "../components/PageHeader";
import Reveal, { EASE } from "../components/Reveal";
import { IMG, GROUP_TRIPS } from "../data/packages";
import { formatINR } from "../lib/api";
import { useEnquiry } from "../components/EnquiryModal";

const ETHOS = [
  { title: "Belong first, travel second", desc: "Groups are curated by pace and vibe — solo travellers, couples and friends who leave as a constellation." },
  { title: "Cap of 12–14", desc: "Small enough for one dinner table, one bonfire, one shared story. Never a tour bus." },
  { title: "Hosted end-to-end", desc: "A trained Cosmos host travels with you and handles everything between the highlights." },
];

export default function GroupTrips() {
  const { openEnquiry } = useEnquiry();

  return (
    <div data-testid="page-group-trips">
      <PageHeader
        testid="group-trips-header"
        eyebrow="Group Trips"
        title="Small groups."
        accent="Unforgettable bonds."
        description="Fixed-departure expeditions for solo travellers and small crews — scheduled, hosted and capped at 14 seats."
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32">
        <div className="grid gap-7 md:grid-cols-2" data-testid="group-trips-grid">
          {GROUP_TRIPS.map((g, i) => {
            const filled = g.groupSize - g.spotsLeft;
            return (
              <Reveal key={g.id} delay={(i % 2) * 0.08}>
                <article
                  data-testid={`group-trip-card-${g.id}`}
                  className="group overflow-hidden rounded-3xl border border-white/10 bg-obsidian transition-all duration-500 hover:border-aurora/40 hover:shadow-[0_20px_60px_rgba(0,245,212,0.12)]"
                >
                  <div className="relative h-60 overflow-hidden md:h-72">
                    <img src={g.image} alt={g.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/20 to-transparent" />
                    <span className="glass absolute left-5 top-5 rounded-full px-3.5 py-1.5 font-mono text-[10px] tracking-[0.2em] text-slate-100">
                      {g.vibe}
                    </span>
                    <div className="absolute bottom-5 left-6 right-6">
                      <h3 className="font-display text-2xl font-extrabold tracking-tight text-slate-50">{g.title}</h3>
                      <p className="mt-1 text-sm text-slate-300">{g.destination}</p>
                    </div>
                  </div>

                  <div className="p-7">
                    <div className="flex flex-wrap gap-x-7 gap-y-3 text-sm text-slate-300">
                      <span className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-aurora" /> {g.dates}
                      </span>
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-aurora" /> {g.nights}N · Max {g.groupSize}
                      </span>
                      <span className="flex items-center gap-2">
                        <Flame className="h-4 w-4 text-comet" /> Led by {g.host}
                      </span>
                    </div>

                    <div className="mt-6">
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${(filled / g.groupSize) * 100}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.1, ease: EASE }}
                          className="h-full rounded-full bg-gradient-to-r from-aurora to-comet"
                        />
                      </div>
                      <p className="mt-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                        {filled} of {g.groupSize} spots filled · {g.spotsLeft} left
                      </p>
                    </div>

                    <div className="mt-7 flex items-center justify-between border-t border-white/5 pt-5">
                      <div>
                        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Per person</p>
                        <p className="font-display text-xl font-extrabold text-slate-50">{formatINR(g.price)}</p>
                      </div>
                      <button
                        data-testid={`group-trip-reserve-btn-${g.id}`}
                        onClick={() => openEnquiry({ tripType: "Group Trip", destination: `${g.title} · ${g.dates}` })}
                        className="rounded-full bg-aurora px-6 py-3 text-xs font-bold text-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,245,212,0.4)] hover:-translate-y-0.5"
                      >
                        Reserve Your Spot
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-20">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
            <img src={IMG.expedition} alt="Group of travellers on an expedition" loading="lazy" className="h-72 w-full object-cover md:h-80" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center gap-8 p-8 md:flex-row md:items-center md:justify-between md:p-14">
              <div className="max-w-lg">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">The Belonging Ethos</p>
                <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-slate-50 md:text-4xl">
                  Nobody travels alone <em className="font-editorial font-medium italic text-comet">here.</em>
                </h2>
              </div>
              <div className="grid gap-5 sm:grid-cols-3 md:max-w-xl">
                {ETHOS.map((e) => (
                  <div key={e.title}>
                    <h3 className="font-display text-sm font-bold text-aurora">{e.title}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-slate-300">{e.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
